if (!customElements.get('product-coupon')) {
  customElements.define(
    'product-coupon',
    class ProductCoupon extends HTMLElement {
      static storageKey = 'product-coupon-codes';
      static cartCodes = null;
      static cartCodesPromise = null;

      connectedCallback() {
        this.input = this.querySelector('.product-coupon__input');
        this.price = this.querySelector('[data-discounted-price]');
        this.status = this.querySelector('[data-coupon-status]');
        this.input?.addEventListener('change', this.onChange);
        this.restoreSelection();

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId || !event.data.variant) return;
            this.updatePrice(event.data.variant.price);
          });
        }
      }

      disconnectedCallback() {
        this.input?.removeEventListener('change', this.onChange);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onChange = async () => {
        const shouldApply = this.input.checked;
        this.price?.classList.toggle('product-coupon__discounted-price--hidden', !this.input.checked);
        this.setLoading(true);
        this.setStatus(this.dataset.applyingText);

        try {
          const cartCodes = await ProductCoupon.getCartCodes();
          const code = this.input.dataset.code?.trim();
          if (!code) throw new Error('A discount code is required.');

          const pageCodes = ProductCoupon.getSelectedPageCodes();
          const nextCodes = new Set(cartCodes);

          document.querySelectorAll('product-coupon .product-coupon__input[data-code]').forEach((input) => {
            const pageCode = input.dataset.code?.trim();
            if (pageCode) nextCodes.delete(pageCode.toLowerCase());
          });

          pageCodes.forEach((pageCode) => nextCodes.add(pageCode));

          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ discount: Array.from(nextCodes).join(',') })
          });

          if (!response.ok) throw new Error('The discount request failed.');

          await response.json();
          ProductCoupon.cartCodes = nextCodes;
          ProductCoupon.saveSelectedPageCodes(pageCodes);
          this.setStatus(shouldApply ? this.dataset.appliedText : this.dataset.removedText, 'success');

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-coupon' });
          }
        } catch (error) {
          this.input.checked = !shouldApply;
          this.price?.classList.toggle('product-coupon__discounted-price--hidden', !this.input.checked);
          this.setStatus(this.dataset.errorText, 'error');
          console.error(error);
        } finally {
          this.setLoading(false);
        }
      };

      restoreSelection() {
        if (!this.input?.dataset.code) return;
        const savedCodes = ProductCoupon.getSavedSelectedPageCodes();
        const isSelected = savedCodes.has(this.input.dataset.code.trim().toLowerCase());
        this.input.checked = isSelected;
        this.price?.classList.toggle('product-coupon__discounted-price--hidden', !isSelected);
        if (isSelected) this.setStatus(this.dataset.appliedText, 'success');
      }

      setLoading(isLoading) {
        this.input.disabled = isLoading;
        this.querySelector('.product-coupon__row')?.setAttribute('aria-busy', String(isLoading));
      }

      setStatus(message, state = '') {
        if (!this.status) return;
        this.status.textContent = message || '';
        if (state) {
          this.status.dataset.state = state;
        } else {
          delete this.status.dataset.state;
        }
      }

      static getSelectedPageCodes() {
        return new Set(
          Array.from(document.querySelectorAll('product-coupon .product-coupon__input[data-code]:checked'))
            .map((input) => input.dataset.code?.trim().toLowerCase())
            .filter(Boolean)
        );
      }

      static getSavedSelectedPageCodes() {
        try {
          return new Set(JSON.parse(sessionStorage.getItem(ProductCoupon.storageKey) || '[]'));
        } catch (error) {
          return new Set();
        }
      }

      static saveSelectedPageCodes(codes) {
        try {
          sessionStorage.setItem(ProductCoupon.storageKey, JSON.stringify(Array.from(codes)));
        } catch (error) {
          // The cart still keeps the applied code when browser storage is unavailable.
        }
      }

      static getCartCodes() {
        if (ProductCoupon.cartCodes) return Promise.resolve(ProductCoupon.cartCodes);
        if (ProductCoupon.cartCodesPromise) return ProductCoupon.cartCodesPromise;

        ProductCoupon.cartCodesPromise = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }
        })
          .then((response) => {
            if (!response.ok) throw new Error('The cart could not be loaded.');
            return response.json();
          })
          .then((cart) => {
            const applications = [
              ...(cart.discount_applications || []),
              ...(cart.cart_level_discount_applications || [])
            ];
            const codes = applications
              .filter((discount) => discount.type === 'discount_code')
              .map((discount) => discount.title?.trim().toLowerCase())
              .filter(Boolean);

            ProductCoupon.getSavedSelectedPageCodes().forEach((code) => codes.push(code));
            ProductCoupon.cartCodes = new Set(codes);
            return ProductCoupon.cartCodes;
          })
          .finally(() => {
            ProductCoupon.cartCodesPromise = null;
          });

        return ProductCoupon.cartCodesPromise;
      }

      updatePrice(price) {
        const value = Number(this.dataset.discountValue);
        let discountedPrice = price;

        if (this.dataset.valueType === 'percentage') {
          discountedPrice = Math.round(price * (1 - value / 100));
        } else {
          discountedPrice = Math.max(0, price - Math.round(value * 100));
        }

        const target = this.price?.querySelector('strong');
        if (target) target.textContent = this.formatMoney(discountedPrice);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const type = placeholder ? placeholder[1] : 'amount';
        const amount = cents / 100;
        let value;

        if (type === 'amount_no_decimals') {
          value = Math.round(amount).toLocaleString();
        } else if (type === 'amount_with_comma_separator') {
          value = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        } else {
          value = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        return placeholder ? format.replace(placeholder[0], value) : value;
      }
    }
  );
}
